import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Form, InputNumber, Select } from '@arco-design/web-react';

import { LARGE_RANGE_SLIDER_MARK_COUNT } from '@/components/CapacitySlider';
import {
  GPU_OPTIONS_FINGERPRINT_FIELD,
  GPU_SELECTED_ARCHITECTURE_FIELD,
  fetchGpuInventoryForArchitecture,
  fingerprintGpuOptions,
  setFormFieldError,
  shouldStampGpuOptionsOnModalConfirm,
  stampGpuOptionsFingerprint,
} from '@/delivery-type-plugin/capabilities/image';
import { NicResourceBlock } from '@/delivery-type-plugin/capabilities/maas-node-groups/group-spec-modal/NicResourceBlock';
import { SharedMemoryResourceBlock } from '@/delivery-type-plugin/capabilities/shared-memory/SharedMemoryResourceBlock';

import { ResourceConfigAddCard, ResourceConfigCard } from '../../../components/resourceConfig';
import { buildVendorDeviceMetaWatchFields } from '../../../components/vendorDevice';
import { isFieldDisabled, isFieldReadonly } from '../../../platform/capability-registry/fieldControl';
import { isActionVisible, isFieldVisible } from '../../../platform/capability-registry/resolveFieldMode';
import { shouldSkipCapabilityFetch } from '../../../platform/capability-registry/shouldSkipCapabilityFetch';
import { useOptionalResourceFormContext } from '../../../platform/resource-form';
import { useScheduleResourceFormPriceRefresh } from '../../../platform/resource-form';
import { COMPUTE_RESOURCE_ADD_LABEL, COMPUTE_RESOURCE_ICON } from '../constants';
import { hasBandwidthValue } from '../domain/bandwidth';
import { resolveVendorDevicePath } from '../domain/paths';
import { hasComputeResourceFieldError, resolveComputeResourceValidateFields } from '../domain/requiredRules';
import { resolveComputeResourceCodes } from '../domain/resourceCodes';
import { buildCpuSummaryItems, buildGpuSummaryItems, buildMemorySummaryItems, hasComputeResourceFormData } from '../domain/summary';
import { COMPUTE_ACTION_KEYS, COMPUTE_FIELD_KEYS } from '../fieldKeys';
import { ComputeResourcePersistFields } from '../fields/ComputeResourcePersistFields';
import { useComputeResourceLimits } from '../hooks/useComputeResourceLimits';
import styles from '../index.module.less';
import { applyModalFormValues, buildModalFormValues } from '../modal/modalFormBridge';
import type { ComputeBlockProps, ComputeModalResourceKind, ComputePaths } from '../types';
import { ComputeResourceConfigModal } from './ComputeResourceConfigModal';
import { isComputeResourceReadonly } from './fieldMode';
import { useClearGpuOnArchitectureChange } from './useClearGpuOnArchitectureChange';
import { useComputeBlockState } from './useComputeBlockState';

const FormItem = Form.Item;
const DEFAULT_ARCH_OPTIONS = ['AMD64', 'ARM64'];

/** 计算资源能力块：架构 + CPU/内存/GPU 卡片/弹窗 + 带宽 */
export function ComputeBlock(props: ComputeBlockProps) {
  const { form, paths: rawPaths, options, linkageProps, insertBeforeGpu, ...control } = props;
  const paths = rawPaths as ComputePaths;
  const { t } = useTranslation('cluster');

  const gpuVariant = options?.gpuVariant ?? 'cloud-host';
  const requireGpuSoftware = options?.requireGpuSoftware ?? gpuVariant !== 'container';
  const resourceCodes = resolveComputeResourceCodes(options);
  const showGpu = options?.showGpu !== false;
  const showBandwidth = options?.showBandwidth !== false;
  const billModelId = options?.billModelId;
  const deploymentTypeId = options?.deploymentTypeId;
  const paymentTiming = options?.paymentTiming;
  const skuType = options?.skuType;
  const resourceEditMode = options?.resourceEditMode;
  const showSharedMemory = options?.showSharedMemory === true;
  const showNic = options?.showNic === true;
  const nicsField = options?.nicsField ?? 'network.nics';
  const nicEnabledField = options?.nicEnabledField ?? '_nicEnabled';
  const resourceFormCtx = useOptionalResourceFormContext();
  const parentForm = options?.parentForm;
  const presetConfigSnapshot = options?.presetConfigSnapshot ?? resourceFormCtx?.presetConfigSnapshot;
  const lockActionsWhenFilled = options?.lockActionsWhenFilled ?? resourceFormCtx?.lockActionsWhenFilled;

  const namespace = linkageProps?.namespace;
  const vmName = linkageProps?.vmName;
  const archOptions = useMemo(
    () => (linkageProps?.architectureOptions?.length ? linkageProps.architectureOptions : DEFAULT_ARCH_OPTIONS),
    [linkageProps?.architectureOptions],
  );

  const skipResourceLimitFetch = shouldSkipCapabilityFetch(control, {
    skipFetch: options?.skipFetch,
    fieldKeys: [COMPUTE_FIELD_KEYS.cpu, COMPUTE_FIELD_KEYS.memory, COMPUTE_FIELD_KEYS.gpu],
  });
  const { cpuMax, memoryMax } = useComputeResourceLimits({
    namespace,
    vmName,
    enabled: !skipResourceLimitFetch,
  });
  const { activeModal, openModal, closeModal, handleRemoveResource } = useComputeBlockState({ form, paths });

  const archReadonly = isFieldReadonly(COMPUTE_FIELD_KEYS.architecture, control);
  const archDisabled = isFieldDisabled(COMPUTE_FIELD_KEYS.architecture, control) || archReadonly;
  const bandwidthReadonly = isFieldReadonly(COMPUTE_FIELD_KEYS.bandwidth, control);

  const cpuMin = linkageProps?.cpuMin ?? 1;
  const memoryMin = linkageProps?.memoryMin ?? options?.memoryMin ?? 1;
  const sharedMemoryMin = linkageProps?.sharedMemoryMin;
  const memoryMarkCount = options?.memoryMarkCount ?? LARGE_RANGE_SLIDER_MARK_COUNT;
  const resolvedMemoryMax = options?.memoryMax ?? (memoryMax || 2048);

  const architecture = Form.useWatch(paths.architecture, form) as string | undefined;
  const archFieldVisible = isFieldVisible(COMPUTE_FIELD_KEYS.architecture, control);
  useClearGpuOnArchitectureChange(form, paths, architecture, archFieldVisible && options?.clearGpuOnArchitectureChange !== false);

  /** 架构仅一项时默认选中（含镜像限制只剩一种架构） */
  useEffect(() => {
    if (!archFieldVisible || archOptions.length !== 1) return;
    const only = archOptions[0];
    if (!only || architecture === only) return;
    form.setFieldValue(paths.architecture, only);
  }, [archFieldVisible, archOptions, architecture, form, paths.architecture]);

  const bandwidthValue = Form.useWatch(paths.bandwidth, form);
  const showBandwidthField =
    showBandwidth && isFieldVisible(COMPUTE_FIELD_KEYS.bandwidth, control) && (!bandwidthReadonly || hasBandwidthValue(bandwidthValue));

  const isContainerCompute = gpuVariant === 'container';
  const cpuSimpleMode = isContainerCompute;
  const resourceWatchFields = useMemo(() => {
    const base = (
      isContainerCompute
        ? [paths.cpuRequest, paths.memory, paths.gpu]
        : [paths.cpuCards, paths.cpuCores, paths.cpuLogical, paths.memory, paths.gpu]
    ).filter((field): field is string => Boolean(field));
    if (!billModelId) return base;
    /** 数量 + 厂商设备叶子字段变更需触发卡片态刷新（仅 watch 父对象时常不刷新） */
    return [
      ...base,
      `${paths.gpu}.number`,
      `${paths.gpu}.vendorID`,
      `${paths.gpu}.deviceID`,
      ...buildVendorDeviceMetaWatchFields(paths.cpuPricing),
      ...buildVendorDeviceMetaWatchFields(paths.memoryPricing),
    ];
  }, [billModelId, isContainerCompute, paths]);
  const resourceFormValues = Form.useWatch(resourceWatchFields, form) as Record<string, unknown> | undefined;
  const schedulePriceRefresh = useScheduleResourceFormPriceRefresh();

  const handleModalOk = async (modalValues: Record<string, unknown>) => {
    if (activeModal == null) return;
    const previousGpu = activeModal === 'gpu' ? form.getFieldValue(paths.gpu) : undefined;
    const stampedFingerprint = String(form.getFieldValue(GPU_OPTIONS_FINGERPRINT_FIELD) ?? '').trim();
    applyModalFormValues(form, paths, activeModal, modalValues, { cpuSimpleMode });
    if (activeModal === 'gpu' && architecture) {
      form.setFieldValue(GPU_SELECTED_ARCHITECTURE_FIELD, architecture);
      const nextGpu = form.getFieldValue(paths.gpu);
      let currentFingerprint: string | undefined;
      let currentInventory: Record<string, unknown>[] | undefined;
      try {
        currentInventory = await fetchGpuInventoryForArchitecture(architecture);
        if (currentInventory) currentFingerprint = fingerprintGpuOptions(currentInventory);
      } catch (error) {
        console.log(error);
      }
      const shouldStamp = shouldStampGpuOptionsOnModalConfirm({
        previousGpu,
        nextGpu,
        stampedFingerprint,
        currentFingerprint,
        currentInventory,
      });
      if (!shouldStamp) {
        setFormFieldError(form, `${paths.gpu}.number`, t('data-center:gpu.reselect.after.architecture.change'));
        closeModal();
        schedulePriceRefresh(form);
        return;
      }
      if (currentFingerprint) {
        form.setFieldValue(GPU_OPTIONS_FINGERPRINT_FIELD, currentFingerprint);
      } else {
        await stampGpuOptionsFingerprint(form, architecture);
      }
    }
    closeModal();
    /** 自定义规格改数量：写回后主动询价，不单靠 Form.useWatch */
    schedulePriceRefresh(form);
  };

  const pathsKey = JSON.stringify(paths);

  const modalInitialValues = useMemo(() => {
    if (activeModal == null) return undefined;
    return buildModalFormValues(form.getFieldsValue(), paths, activeModal, { memoryMin, cpuMin, cpuSimpleMode });
  }, [activeModal, cpuMin, cpuSimpleMode, form, memoryMin, pathsKey]);

  /** 有规格或厂商设备任一即展示卡片（完整校验仍走 requiredRule） */
  const showCpuCard = hasComputeResourceFormData('cpu', resourceFormValues ?? {}, paths);
  const showMemoryCard = hasComputeResourceFormData('memory', resourceFormValues ?? {}, paths);
  const showGpuCard = hasComputeResourceFormData('gpu', resourceFormValues ?? {}, paths);

  const coreLabel = t('core-pl');
  const workloadType = options?.vendorDeviceWorkloadType ?? 'VirtualMachine';

  if (
    !isFieldVisible(COMPUTE_FIELD_KEYS.architecture, control) &&
    !isFieldVisible(COMPUTE_FIELD_KEYS.cpu, control) &&
    !isFieldVisible(COMPUTE_FIELD_KEYS.memory, control) &&
    !isFieldVisible(COMPUTE_FIELD_KEYS.gpu, control) &&
    !isFieldVisible(COMPUTE_FIELD_KEYS.bandwidth, control)
  ) {
    return null;
  }

  const renderAddButton = (kind: ComputeModalResourceKind, label: string, icon: string) => {
    if (isComputeResourceReadonly(kind, control)) return null;
    const validateFields = resolveComputeResourceValidateFields(kind, paths, cpuSimpleMode);
    return (
      <FormItem shouldUpdate noStyle>
        {(_, formInstance) => {
          const hasError = hasComputeResourceFieldError(formInstance, validateFields);
          return (
            <ResourceConfigAddCard
              resourceCode={resourceCodes[kind]}
              icon={icon}
              title={hasError ? `${label}（必填）` : label}
              hasError={hasError}
              onAdd={() => openModal(kind)}
            />
          );
        }}
      </FormItem>
    );
  };

  const renderResourceCard = (
    kind: ComputeModalResourceKind,
    visible: boolean,
    configured: boolean,
    items: ReturnType<typeof buildCpuSummaryItems>,
    label: string,
    icon: string,
  ) => {
    if (!visible) return null;
    const resourceReadonly = isComputeResourceReadonly(kind, control);
    if (!configured) return renderAddButton(kind, label, icon);
    const canEdit = !resourceReadonly && isActionVisible(COMPUTE_ACTION_KEYS.edit, control);
    const canDelete = !resourceReadonly && isActionVisible(COMPUTE_ACTION_KEYS.remove, control);
    const validateFields = resolveComputeResourceValidateFields(kind, paths, cpuSimpleMode);
    return (
      <FormItem shouldUpdate noStyle>
        {(_, formInstance) => {
          const hasError = hasComputeResourceFieldError(formInstance, validateFields);
          return (
            <ResourceConfigCard
              resourceCode={resourceCodes[kind]}
              /**
               * GPU（及计价 CPU/内存）厂商设备已由 ComputeResourcePersistFields 常驻挂载，避免重复 FormItem；
               * 非计价 CPU/内存仍挂在卡片上（弹窗内选型区 hidden，仅作字段收集兜底）。
               */
              vendorDevicePrefix={billModelId || kind === 'gpu' ? undefined : resolveVendorDevicePath(kind, paths)}
              icon={icon}
              title={label}
              items={items}
              canEdit={canEdit}
              canDelete={canDelete}
              hasError={hasError}
              onEdit={() => openModal(kind)}
              onDelete={() => handleRemoveResource(kind)}
            />
          );
        }}
      </FormItem>
    );
  };

  return (
    <>
      {isFieldVisible(COMPUTE_FIELD_KEYS.architecture, control) ? (
        <FormItem
          label={t('common:architecture', { ns: 'common', defaultValue: '架构' })}
          field={paths.architecture}
          rules={archDisabled ? undefined : [{ required: true, message: t('must-select') }]}
          disabled={archDisabled}
        >
          <Select
            placeholder={t('common:select.placeholder', { ns: 'common' })}
            options={archOptions.map((item) => ({ label: item, value: item }))}
            disabled={archDisabled}
          />
        </FormItem>
      ) : null}

      <div className={styles.resourceList}>
        {renderResourceCard(
          'cpu',
          isFieldVisible(COMPUTE_FIELD_KEYS.cpu, control),
          showCpuCard,
          buildCpuSummaryItems(form, paths, coreLabel, Boolean(billModelId)),
          COMPUTE_RESOURCE_ADD_LABEL.cpu,
          COMPUTE_RESOURCE_ICON.cpu,
        )}
        {renderResourceCard(
          'memory',
          isFieldVisible(COMPUTE_FIELD_KEYS.memory, control),
          showMemoryCard,
          buildMemorySummaryItems(form, paths, Boolean(billModelId)),
          COMPUTE_RESOURCE_ADD_LABEL.memory,
          COMPUTE_RESOURCE_ICON.memory,
        )}
        {showGpu
          ? renderResourceCard(
              'gpu',
              isFieldVisible(COMPUTE_FIELD_KEYS.gpu, control),
              showGpuCard,
              buildGpuSummaryItems(form, paths, Boolean(billModelId)),
              COMPUTE_RESOURCE_ADD_LABEL.gpu,
              COMPUTE_RESOURCE_ICON.gpu,
            )
          : null}
        {insertBeforeGpu}
        {showSharedMemory ? <SharedMemoryResourceBlock form={form} resourceEditMode={resourceEditMode} minGi={sharedMemoryMin} /> : null}
        {showNic ? (
          <NicResourceBlock
            form={form}
            parentForm={parentForm}
            nicsField={nicsField}
            enabledField={nicEnabledField}
            billModelId={billModelId}
            deploymentTypeId={deploymentTypeId}
            paymentTiming={paymentTiming}
            skuType={skuType}
            resourceEditMode={resourceEditMode}
            lockActionsWhenFilled={lockActionsWhenFilled}
            presetConfigSnapshot={presetConfigSnapshot}
            nicPricingOptions={options?.nicPricingOptions}
          />
        ) : null}
      </div>

      {showBandwidthField ? (
        <FormItem label={t('brand')} field={paths.bandwidth} disabled={bandwidthReadonly}>
          <InputNumber min={0} mode="button" placeholder={t('place', { name: t('brand') })} disabled={bandwidthReadonly} />
        </FormItem>
      ) : null}

      <ComputeResourcePersistFields
        form={form}
        paths={paths}
        billModelId={billModelId}
        paymentTiming={paymentTiming}
        skuType={skuType}
        resourceEditMode={resourceEditMode}
        requireCpu={isFieldVisible(COMPUTE_FIELD_KEYS.cpu, control) && !isFieldReadonly(COMPUTE_FIELD_KEYS.cpu, control)}
        requireMemory={isFieldVisible(COMPUTE_FIELD_KEYS.memory, control) && !isFieldReadonly(COMPUTE_FIELD_KEYS.memory, control)}
        cpuRequiredMessage={t('required', { content: COMPUTE_RESOURCE_ADD_LABEL.cpu })}
        memoryRequiredMessage={t('required', { content: t('mem') })}
      />

      <FormItem field={paths.hpcRdma} hidden>
        <Select showSearch mode="multiple" />
      </FormItem>
      <FormItem field={GPU_SELECTED_ARCHITECTURE_FIELD} hidden>
        <input />
      </FormItem>
      <FormItem field={GPU_OPTIONS_FINGERPRINT_FIELD} hidden>
        <input />
      </FormItem>

      {activeModal != null ? (
        <ComputeResourceConfigModal
          activeModal={activeModal}
          billModelId={billModelId}
          deploymentTypeId={deploymentTypeId}
          paymentTiming={paymentTiming}
          skuType={skuType}
          resourceEditMode={resourceEditMode}
          initialValues={modalInitialValues}
          architecture={architecture}
          resourceCodes={resourceCodes}
          gpuVariant={gpuVariant}
          memoryMin={memoryMin}
          memoryMax={resolvedMemoryMax}
          memoryMarkCount={memoryMarkCount}
          cpuMax={cpuMax}
          cpuMin={cpuMin}
          cpuSimpleMode={cpuSimpleMode}
          workloadType={workloadType}
          requireGpuSoftware={requireGpuSoftware}
          onOk={handleModalOk}
          onCancel={closeModal}
        />
      ) : null}
    </>
  );
}
